import { useState, type CSSProperties, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import AuthScreen from '../components/AuthScreen';
import { brand, brandAppName } from '../brand';

/**
 * WhatsApp 登記（`decisions/006`；產品負責人 2026-10-06 重寫）
 *
 * 流程：填【姓名＋公司】→ 一按即開 WhatsApp（訊息已預填）→ **用戶按送出** →
 *      webhook（驗簽名）用發訊號碼建會員（姓名／公司由訊息解析）→ 回覆一次性登入連結 → 一按入 app。
 * ⚠️ WhatsApp 唔可以代人自動送出（平台/瀏覽器限制）→ 用戶最少要按兩下（送出 ＋ 點連結）。
 * ⚠️ 頁面刻意**冇**開場白／步驟／注意事項（產品負責人指定：簡潔）。
 * WhatsApp 號碼來自 build-time `VITE_WA_NUMBER`（`gen-brand.mjs` 會驗同 brand.json 一致）。
 */
const WA_NUMBER = (import.meta.env.VITE_WA_NUMBER as string | undefined) ?? '';

const inputStyle: CSSProperties = {
  width: '100%',
  borderRadius: 8,
  padding: '8px 12px',
  border: '1px solid var(--color-divider)',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
};

export default function JoinPage() {
  const { t, i18n } = useTranslation();
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [error, setError] = useState('');

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const n = name.trim();
    if (n.length < 2) {
      setError(t('join_name_required'));
      return;
    }
    if (!WA_NUMBER) {
      setError(t('join_wa_unavailable'));
      return;
    }
    /* 訊息預填：server 解析 `姓名：`／`公司：`（公司可留空） */
    const text = t('join_wa_prefill', { name: n, company: company.trim() });
    window.location.href = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(text)}`;
  }

  return (
    <AuthScreen>
      <form
        onSubmit={onSubmit}
        className="max-w-lg w-full rounded-xl px-8 pb-8 pt-10"
        style={{ background: 'var(--color-card)', boxShadow: 'var(--shadow-soft)' }}
      >
            <img
              src={brand.logos.horizontal}
              alt={brandAppName(i18n.language)}
              style={{ width: '100%', maxWidth: 300, height: 'auto', display: 'block', margin: '0 auto 48px' }}
            />
            <input
              type="text"
              required
              autoComplete="name"
              placeholder={t('join_name_ph')}
              aria-label={t('join_name_ph')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mb-3"
              style={inputStyle}
            />
            <input
              type="text"
              autoComplete="organization"
              placeholder={t('join_company_ph')}
              aria-label={t('join_company_ph')}
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              className="mb-3"
              style={inputStyle}
            />

            {error && (
              <p className="text-sm mb-3" style={{ color: 'var(--color-danger)' }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              className="w-full rounded-lg px-4 py-2 font-semibold"
              style={{ background: 'var(--color-primary)', color: 'var(--color-on-primary)' }}
            >
              {t('join_submit')}
        </button>
      </form>
    </AuthScreen>
  );
}
